import Error from '@/components/Error';
import { RequireParams } from '@/components/RequireParams';
import { FF_MEDICAL_RECORDS_CONSENT_PAGE_V2 } from '@/constants/featureFlags';
import useFeatureFlags from '@/hooks/useFeatureFlags';
import { ConsentMedicalRecords } from '@/pages/ConsentMedicalRecords';
import { VideoConsultation } from '@/pages/consultation/VideoConsultation';
import ConsultEligible from '@/pages/ConsultEligible';
import { ConsultEligibleDoctorForm } from '@/pages/ConsultEligibleDoctor';
import { ScheduleDoctorConsult } from '@/pages/ConsultEligibleDoctorForms/ScheduleDoctorConsult';
import ConsultEligibleNurse from '@/pages/ConsultEligibleNurse';
import { DirectToDoctorForm } from '@/pages/DirectToDoctorForms/DirectToDoctorForm';
import HomeLinks from '@/pages/HomeLinks';
import HomeRedirect from '@/pages/HomeRedirect';
import { MedicalRecordsConsent } from '@/pages/MedicalRecordsConsent';
import { PreConsultationForm } from '@/pages/PreConsultationForms/PreConsultationForm';
import { PRESCREENING_FORM_EMAIL_LOCAL_STORAGE_KEY } from '@/pages/PreScreeningForms/PreScreeningForm';
import { PreScreeningFormVariants } from '@/pages/PreScreeningForms/PreScreeningFormVariants';
import { TriageForm } from '@/pages/TriageForm/TriageForm';
import Layout from '@/pcp/components/Layout';
import { Route, Routes } from 'react-router-dom';

export type AppRoutesProps = {
  targetForm: string;
  isProduction: boolean;
};

// Pre-screening leaves the patient's email behind, so a lost query string does
// not turn them away.
const PATIENT_EMAIL_FALLBACK = { email: PRESCREENING_FORM_EMAIL_LOCAL_STORAGE_KEY };

const Error404 = () => (
  <Layout contentMaxWidth="md">
    <Error type="404" />
  </Layout>
);

export const AppRoutes = ({ targetForm, isProduction }: AppRoutesProps) => {
  const queryParams = new URLSearchParams(location.search);

  const { flags } = useFeatureFlags();
  const enable = queryParams.get('enable');
  const hasPageWrapper = enable !== 'embed';

  const getRootComponent = () => {
    switch (targetForm) {
      case 'screening':
        return <PreScreeningFormVariants />;

      case 'consult':
        return <PreConsultationForm pageWrapper={hasPageWrapper} showLogo={hasPageWrapper} />;

      case 'portal':
        return <HomeRedirect to="/login" />;

      case 'eligible':
        return <ConsultEligibleNurse />;

      case 'all':
        if (!isProduction) {
          return <HomeLinks />;
        }
        return <HomeRedirect to="/login" />;

      default:
        // fallback for unexpected targetForm
        return <HomeRedirect to="/login" />;
    }
  };

  return (
    <Routes>
      {/* Root (homepage) - dependent on `VITE_FORM_CONFIG` */}
      <Route path="/" element={getRootComponent()} />

      {/* Form paths */}
      {targetForm === 'all' && (
        <>
          <Route path="pre-screening" element={<PreScreeningFormVariants />} />
          <Route
            path="pre-consultation"
            element={<PreConsultationForm pageWrapper={hasPageWrapper} showLogo={hasPageWrapper} />}
          />
        </>
      )}

      {/*
       * Pre-Screening path: the triage form and every outcome it redirects to.
       * These are gated together on purpose — `triage` used to be registered
       * unconditionally, so its complex outcome dead-ended on a 404 in builds
       * that did not register `consult-eligible-nurse`.
       */}
      {(targetForm === 'screening' || !isProduction) && (
        <>
          {/* Entry point the pre-screening site redirects to. */}
          <Route
            path="consult-eligible"
            element={
              <RequireParams params={['email']} fallbackKeys={PATIENT_EMAIL_FALLBACK}>
                <ConsultEligible />
              </RequireParams>
            }
          />
          {/* Consult Eligible page for Nurse */}
          <Route
            path="consult-eligible-nurse"
            element={
              <RequireParams params={['email']} fallbackKeys={PATIENT_EMAIL_FALLBACK}>
                <ConsultEligibleNurse />
              </RequireParams>
            }
          />
          {/* Triage form */}
          <Route
            path="triage/*"
            element={
              <RequireParams params={['email']} fallbackKeys={PATIENT_EMAIL_FALLBACK}>
                <TriageForm />
              </RequireParams>
            }
          />
          {/* Direct to Doctor (D2D) questions */}
          <Route
            path="direct-to-doctor/*"
            element={
              <RequireParams params={['email']} fallbackKeys={PATIENT_EMAIL_FALLBACK}>
                <DirectToDoctorForm />
              </RequireParams>
            }
          />
          {/* Eligible doctor consult, where the D2D questions end */}
          <Route
            path="consult-eligible-doctor"
            element={
              <RequireParams params={['email']} fallbackKeys={PATIENT_EMAIL_FALLBACK}>
                <ConsultEligibleDoctorForm />
              </RequireParams>
            }
          />
          <Route
            path="consult-eligible-doctor/schedule"
            element={
              <RequireParams params={['email']} fallbackKeys={PATIENT_EMAIL_FALLBACK}>
                <ScheduleDoctorConsult />
              </RequireParams>
            }
          />
        </>
      )}

      {/* Add video consultation route */}
      <Route path="video-consultation/:key/*" element={<VideoConsultation />} />

      {/* Medical Records Consent */}
      <Route
        path="medical-records-consent"
        element={flags[FF_MEDICAL_RECORDS_CONSENT_PAGE_V2] ? <MedicalRecordsConsent /> : <ConsentMedicalRecords />}
      />

      {/* Where the forms send a patient whose details are missing. */}
      <Route path="error" element={<Error404 />} />

      {/* All other unmatched paths */}
      <Route path="*" element={<Error404 />} />
    </Routes>
  );
};
